<template>
   <div class="payment-history-page">
      <v-card outlined class="payment-history-card">
         <div class="payment-history-header">
            <div>
               <div class="section-eyebrow">Financial Record</div>
               <h3>Payment History</h3>
               <p>Review captured payments, uploaded receipts, renewal records, and audit references for this member.</p>
            </div>
            <div class="payment-insights">
               <div class="insight-tile">
                  <span>Successful</span>
                  <strong>{{ successCount }}</strong>
               </div>
               <div class="insight-tile">
                  <span>Pending</span>
                  <strong>{{ pendingCount }}</strong>
               </div>
               <div class="insight-tile latest">
                  <span>Latest Payment</span>
                  <strong>{{ latestPaymentDate }}</strong>
               </div>
            </div>
         </div>

         <v-alert v-if="message" dense text :type="messageType" class="mx-4 mb-4">
            {{ message }}
         </v-alert>

         <div v-if="loading" class="loading-row">
            <v-progress-circular indeterminate size="24" width="3" color="primary" class="mr-3"></v-progress-circular>
            Loading payment history...
         </div>

         <v-simple-table v-else class="payment-ledger-table">
            <template v-slot:default>
               <thead>
                  <tr>
                     <th class="text-left">Payment For</th>
                     <th class="text-left">Method</th>
                     <th class="text-left">Payment Date</th>
                     <th class="text-right">Amount</th>
                     <th class="text-left">Reference</th>
                     <th class="text-left">Status</th>
                     <th class="text-left">Receipt</th>
                  </tr>
               </thead>
               <tbody v-if="payments.length > 0">
                  <tr v-for="item in payments" :key="item.tabletype + '-' + item.id">
                     <td>
                        <strong>{{ paymentPurpose(item) }}</strong>
                        <span>{{ item.description || '-' }}</span>
                     </td>
                     <td>
                        <v-chip x-small outlined :color="paymentMethodColor(item)">
                           {{ paymentMethodLabel(item) }}
                        </v-chip>
                     </td>
                     <td>{{ formatDate(item.paymentdate || item.created_at) }}</td>
                     <td class="text-right amount-cell">RM {{ Number(item.amount || 0).toFixed(2) }}</td>
                     <td>
                        <strong>{{ item.receipt_no || item.trxnid || '-' }}</strong>
                        <span>Year {{ item.payyear || '-' }}</span>
                     </td>
                     <td>
                        <v-chip x-small dark :color="paymentStatusColor(item)">
                           {{ paymentStatusLabel(item) }}
                        </v-chip>
                     </td>
                     <td>
                        <a v-if="receiptUrl(item)" :href="receiptUrl(item)" target="NEW">View receipt</a>
                        <span v-else>-</span>
                     </td>
                  </tr>
               </tbody>
               <tbody v-else>
                  <tr>
                     <td colspan="7" class="empty-state">
                        <v-icon size="36" color="blue-grey lighten-1">mdi-credit-card-search-outline</v-icon>
                        <div>No payment record has been captured for this member.</div>
                     </td>
                  </tr>
               </tbody>
            </template>
         </v-simple-table>
      </v-card>
   </div>
</template>

<script>
const axios = require('axios')

export default {
   name: "TablePaymentReadOnly",

   data() {
      return {
         membership: null,
         userid: 0,
         payments: [],
         haspaid: false,
         submitting: false,
         loading: false,
         message: '',
         messageType: 'info',
      }
   },

   computed: {
      successCount() {
         return (this.payments || []).filter(item => this.paymentStatusLabel(item) === 'Success').length
      },

      pendingCount() {
         return (this.payments || []).filter(item => this.paymentStatusLabel(item) === 'Pending').length
      },

      latestPaymentDate() {
         if (!this.payments || this.payments.length === 0) {
            return '-'
         }
         return this.formatDate(this.payments[0].paymentdate || this.payments[0].created_at)
      },
   },

   mounted: function () {
      this.$emit('PaymentMounted')
   },

   methods: {
      setuserid(pUserID) {
         this.userid = pUserID
         this.initialize()
      },

      initialize() {
         this.$nextTick(() => {
            this.loadmemberinfo()
            this.loadpaymentinfo()
         })
      },

      formatDate(e) {
         if (!e) {
            return '-'
         }
         return this.$moment(new Date(e)).format('DD/MM/YYYY @ h:mm A')
      },

      loadmemberinfo() {
         var url = '/member/info/' + this.userid
         axios.get(url)
         .then((resp) => {
            var rtn = resp.data
            if (rtn.status === 'OK') {
               this.membership = rtn.data
            }
         })
      },

      loadpaymentinfo() {
         this.loading = true
         this.message = ''
         axios.post('/admin/member/payments/list', {
            userid: this.userid
         })
         .then((resp) => {
            if (resp.data) {
               var rtn = resp.data
               if (rtn.status === 'OK') {
                  this.payments = rtn.data || []
                  if (this.payments.length > 0) {
                     this.haspaid = true
                     this.$emit('PaymentCompleted')
                  } else {
                     this.haspaid = false
                  }
               } else {
                  this.messageType = 'error'
                  this.message = 'Unable to load payment records. Please check the database schema or server log.'
               }
            }
         })
         .catch(() => {
            this.messageType = 'error'
            this.message = 'Unable to load payment records.'
         })
         .finally(() => {
            this.loading = false
         })
      },

      paymentPurpose(item) {
         const text = String(item.description || '').toLowerCase()
         if (text.indexOf('corporate upgrade') !== -1) return 'Corporate Upgrade'
         if (text.indexOf('graduate upgrade') !== -1) return 'Graduate Upgrade'
         if (String(item.paymenttype || '') === 'SF') return 'Annual Fee'
         if (String(item.paymenttype || '') === 'EFSF') return 'Entrance Fee + Annual Fee'
         if (String(item.paymenttype || '') === 'EF') return 'Entrance Fee'
         return item.paymenttype || 'Membership Fee'
      },

      paymentStatusLabel(item) {
         const status = String(item.trxnstatus || '')
         if (status === '01') return 'Success'
         if (status === '00' || status === '02') return 'Pending'
         if (status === '03') return 'Failed'
         return status || '-'
      },

      paymentStatusColor(item) {
         const label = this.paymentStatusLabel(item)
         if (label === 'Success') return 'green darken-1'
         if (label === 'Pending') return 'orange darken-1'
         if (label === 'Failed') return 'red darken-1'
         return 'blue-grey'
      },

      paymentMethodLabel(item) {
         const method = String(item.paymentmethod || '').toUpperCase()
         if (method === 'MANUAL') return 'Manual Receipt'
         if (method === 'ADMIN') return 'Manual by Admin'
         if (method === 'TOYYIBPAY') return 'ToyyibPay'
         if (item.tabletype === 'renewals') return 'ToyyibPay'
         return method || '-'
      },

      paymentMethodColor(item) {
         const label = this.paymentMethodLabel(item)
         if (label === 'ToyyibPay') return 'primary'
         if (label === 'Manual by Admin') return 'teal'
         if (label === 'Manual Receipt') return 'deep-purple'
         return 'blue-grey'
      },

      receiptUrl(item) {
         if (String(item.trxnstatus || '') !== '01') {
            return ''
         }
         if (item.tabletype === 'renewals') {
            return '/member/payment/renewal/receipt/' + encodeURIComponent(item.id)
         }
         return '/member/payment/receipt/' + encodeURIComponent(item.id)
      },

      // sethaspaid() {
      //    var targetPath = '/member/payment/haspaid'
      //    var data = {
      //       userid: this.userid
      //    }
      //    axios.post(targetPath, data)
      //    .then((resp) => {
      //       if (resp.data) {
      //          var rtn = resp.data
      //          if (rtn.status !== 'OK') {
      //             console.log(rtn.status + ': ' + rtn.error)
      //          }
      //       }
      //    })
      // },

      // registerreceipt() {
      //    var targetpath = "/member/payment/register"

      //    var data = new FormData()
      //    data.append('userid', this.userid)
      //    data.append('description', this.form.description)
      //    data.append('membertype', this.form.membertype)
      //    data.append('paymenttype', 'EFSF') // Entrance Fee + Subscription Fee
      //    data.append('amount', this.form.amount)
      //    data.append('trxnid', this.form.trxnid)
      //    if (this.form.document) {
      //       data.append('document', this.form.document)
      //    }

      //    this.submitting = true
      //    axios.post(targetpath, data, {
      //       headers: {
      //          'Content-Type': 'multipart/form-data'
      //       }
      //    })
      //    .then((resp) => {
      //       if (resp.data) {
      //          this.submitting = false
      //          var rtn = resp.data
      //          if (rtn.status === 'OK') {
      //             this.initialize()
      //             this.sethaspaid()
      //             this.dlgregreceipt = false
      //             this.$emit('PaymentCompleted')
      //          } else {
      //             console.log(rtn.status + ': ' + rtn.error)
      //          }
      //       }
      //    })

      // }
   }

}
</script>

<style scoped>
.payment-history-page {
   background: #f8fafc;
   min-height: calc(100vh - 112px);
   padding: 18px;
}

.payment-history-card {
   border: 1px solid #dce6f2 !important;
   border-radius: 8px;
   overflow: hidden;
}

.payment-history-header {
   align-items: center;
   background: linear-gradient(120deg, #0f766e 0%, #0f5f9e 46%, #1d4ed8 100%);
   color: #fff;
   display: flex;
   justify-content: space-between;
   gap: 16px;
   padding: 20px 22px;
}

.section-eyebrow {
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0;
   opacity: .78;
   text-transform: uppercase;
}

.payment-history-header h3 {
   color: #fff;
   font-size: 22px;
   font-weight: 800;
   margin: 3px 0 4px;
}

.payment-history-header p {
   margin: 0;
   opacity: .86;
}

.payment-insights {
   display: grid;
   gap: 10px;
   grid-template-columns: repeat(3, minmax(120px, 1fr));
   min-width: 460px;
}

.insight-tile {
   background: rgba(255,255,255,.14);
   border: 1px solid rgba(255,255,255,.24);
   border-radius: 8px;
   padding: 10px 12px;
   text-align: left;
}

.insight-tile span {
   display: block;
   font-size: 11px;
   font-weight: 800;
   opacity: .78;
   text-transform: uppercase;
}

.insight-tile strong {
   display: block;
   font-size: 20px;
   line-height: 1.2;
   margin-top: 3px;
}

.insight-tile.latest strong {
   font-size: 13px;
   white-space: nowrap;
}

.payment-ledger-table th {
   background: #f1f5f9;
   color: #475569 !important;
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0;
   text-transform: uppercase;
}

.payment-ledger-table td {
   color: #0f172a;
   vertical-align: middle;
}

.payment-ledger-table td span {
   color: #64748b;
   display: block;
   font-size: 12px;
   margin-top: 2px;
}

.amount-cell {
   color: #0f172a;
   font-weight: 800;
   white-space: nowrap;
}

.loading-row,
.empty-state {
   align-items: center;
   color: #64748b;
   display: flex;
   gap: 10px;
   justify-content: center;
   min-height: 132px;
   text-align: center;
}

@media (max-width: 960px) {
   .payment-history-header {
      align-items: flex-start;
      flex-direction: column;
   }

   .payment-insights {
      grid-template-columns: 1fr;
      min-width: 0;
      width: 100%;
   }

   .insight-tile {
      text-align: left;
      width: 100%;
   }
}
</style>
